import { DeleteTaskButton } from '../accounts/DeleteTaskButton';
import React, { useState } from 'react';
import { Box, Button, Chip, IconButton, Input, Sheet, Textarea, Typography } from '@mui/joy';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { StaffPicker } from '../../../components/admin/StaffPicker';
import type { StaffTask } from '../../../types/admin';

/**
 * One task, with everything you can do to it.
 *
 * The row starts collapsed to a title and a Done button — the common case is
 * ticking something off. Expanding reveals the assignee, the due date and the
 * notes, which is where an automated nudge puts the detail (an NPS detractor's
 * own words, for instance) that would otherwise never be read.
 */
export const TaskRow: React.FC<{
  task: StaffTask;
  onUpdate: (changes: Partial<StaffTask>) => void;
  onComplete: () => void;
  onReopen: () => void;
  isBusy?: boolean;
}> = ({ task, onUpdate, onComplete, onReopen, isBusy }) => {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState<string | null>(null);

  const notesChanged = notes !== null && notes !== task.notes;

  return (
    <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'sm', display: 'grid', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          aria-label={expanded ? 'Hide details' : 'Show details'}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            level="body-sm"
            sx={{ textDecoration: task.is_open ? 'none' : 'line-through' }}
          >
            {task.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {task.assignee_name && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                {task.assignee_name}
              </Typography>
            )}
            {task.due_at && (
              <Typography level="body-xs" color={task.is_overdue ? 'danger' : 'neutral'}>
                Due {new Date(task.due_at).toLocaleDateString()}
              </Typography>
            )}
            {task.created_by_rule && (
              <Chip size="sm" variant="outlined" color="neutral">
                Automatic
              </Chip>
            )}
            {task.notes && !expanded && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
                — {task.notes.split('\n')[0]}
              </Typography>
            )}
          </Box>
        </Box>

        <Button
          size="sm"
          variant="plain"
          color="neutral"
          loading={isBusy}
          onClick={task.is_open ? onComplete : onReopen}
        >
          {task.is_open ? 'Done' : 'Reopen'}
        </Button>
        <DeleteTaskButton task={task} disabled={isBusy} />
      </Box>

      {expanded && (
        <Box sx={{ display: 'grid', gap: 1, pl: 4 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 180px' }}>
              <StaffPicker
                value={task.assignee}
                onChange={(userId) => onUpdate({ assignee: userId })}
                placeholder="Unassigned"
              />
            </Box>
            <Input
              size="sm"
              type="date"
              // The API stores a datetime; a date is what anyone actually sets,
              // so the time of day is left at midnight rather than invented.
              defaultValue={task.due_at ? task.due_at.slice(0, 10) : ''}
              onChange={(event) =>
                onUpdate({ due_at: event.target.value ? `${event.target.value}T09:00:00Z` : null })
              }
              sx={{ flex: '0 0 160px' }}
            />
          </Box>

          <Textarea
            size="sm"
            minRows={2}
            placeholder="Notes"
            value={notes ?? task.notes}
            onChange={(event) => setNotes(event.target.value)}
          />
          {notesChanged && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="sm"
                variant="outlined"
                onClick={() => {
                  onUpdate({ notes: notes as string });
                  setNotes(null);
                }}
              >
                Save notes
              </Button>
            </Box>
          )}

          {task.completed_at && (
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              Completed {new Date(task.completed_at).toLocaleString()}
            </Typography>
          )}
        </Box>
      )}
    </Sheet>
  );
};

export default TaskRow;
